import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ReactComponent as DropdownArrow } from "../../assets/icons/dropdownArrow.svg";
import {
  SelectOption,
  collectionBuildBorderClassName,
  collectionBuildDividerClassName,
} from "./collectionBuildConstants";

type Props = {
  value: string;
  placeholder: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  compact?: boolean;
  variant?: "default" | "silhouette";
  disabled?: boolean;
  /** Shows a filter input at the top of the open menu. */
  searchable?: boolean;
  searchPlaceholder?: string;
};

type MenuRect = {
  top: number;
  left: number;
  width: number;
};

const COMPACT_MENU_MIN_WIDTH = 192;

/** Disabled Fashion / locked query select: Brand Primary Subtle @ 15%, Border/Light (#E9E9E9). */
const QUERY_SELECT_DISABLED_FILL = "bg-[#D3C0B626]";
const QUERY_SELECT_DISABLED_BORDER = "border-light";

/** Query select: 214×32, padding 4×8, radius 8 — Figma Select Inputs. */
const triggerClassName = (
  compact: boolean,
  variant: "default" | "silhouette",
  disabled: boolean
) => {
  const border = disabled
    ? QUERY_SELECT_DISABLED_BORDER
    : collectionBuildBorderClassName;
  if (compact) {
    return `box-border h-8 rounded-lg ${border} py-1 pl-2 ${
      disabled ? "pr-2" : "pr-6"
    } text-sm`;
  }
  if (variant === "silhouette") {
    return `box-border h-10 rounded-lg ${border} pl-3.5 ${
      disabled ? "pr-3.5" : "pr-8"
    } text-sm`;
  }
  // Default query select — match IG fieldControlClassName type size (text-base + text-xs value).
  return `box-border h-8 rounded-lg ${border} py-1 pl-2 ${
    disabled ? "pr-2" : "pr-6"
  } text-base`;
};

const CollectionBuildSelect: React.FC<Props> = ({
  value,
  placeholder,
  options,
  onChange,
  compact = false,
  variant = "default",
  disabled = false,
  searchable = false,
  searchPlaceholder = "Search",
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [menuRect, setMenuRect] = useState<MenuRect | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selected = options.find(option => option.value === value);
  const hasImages = options.some(option => Boolean(option.imageUrl));
  const normalizedQuery = searchable ? searchQuery.trim().toLowerCase() : "";
  const visibleOptions =
    searchable && normalizedQuery
      ? options.filter(option =>
          option.label.toLowerCase().includes(normalizedQuery)
        )
      : options;

  useLayoutEffect(() => {
    if (!open) {
      setMenuRect(null);
      return;
    }

    const updatePosition = () => {
      const trigger = buttonRef.current;
      if (!trigger) {
        return;
      }
      const rect = trigger.getBoundingClientRect();
      const minWidth = compact
        ? COMPACT_MENU_MIN_WIDTH
        : hasImages
          ? Math.max(rect.width, 220)
          : rect.width;
      setMenuRect({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, minWidth),
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    document.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, compact, hasImages]);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
    }
  }, [disabled]);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      return;
    }
    if (!searchable) {
      return;
    }
    const id = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [open, searchable]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="relative min-w-0 w-full overflow-visible" ref={triggerRef}>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) {
            return;
          }
          setOpen(current => !current);
        }}
        className={`relative flex w-full min-w-0 items-center border text-left focus:outline-none disabled:cursor-not-allowed ${triggerClassName(
          compact,
          variant,
          disabled
        )} ${
          disabled ? QUERY_SELECT_DISABLED_FILL : "bg-white"
        } ${selected ? "text-secondary" : "text-tertiary"}`}
      >
        <span
          className={`min-w-0 flex-1 truncate ${
            compact || variant === "silhouette"
              ? ""
              : "whitespace-nowrap text-left text-xs"
          }`}
        >
          {selected?.label || placeholder}
        </span>
        {!disabled ? (
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <span
              className={`transition-transform duration-200 ${
                open ? "rotate-180" : ""
              }`}
            >
              <DropdownArrow className="h-2 w-2 text-secondary" />
            </span>
          </span>
        ) : null}
      </button>
      {open &&
        !disabled &&
        menuRect &&
        createPortal(
          <div
            ref={menuRef}
            className={`fixed z-50 flex max-h-56 min-w-[12rem] flex-col overflow-hidden rounded-xl border ${collectionBuildBorderClassName} bg-white shadow-[0_8px_24px_rgba(0,0,0,0.12)]`}
            style={{
              top: menuRect.top,
              left: menuRect.left,
              width: menuRect.width,
            }}
          >
            {searchable ? (
              <div
                className={`shrink-0 border-b ${collectionBuildDividerClassName} p-2`}
              >
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={event => setSearchQuery(event.target.value)}
                  onClick={event => event.stopPropagation()}
                  onKeyDown={event => {
                    if (event.key === "Escape") {
                      setOpen(false);
                    }
                  }}
                  placeholder={searchPlaceholder}
                  className="w-full rounded-lg border border-light px-2 py-1.5 text-sm text-secondary placeholder:text-tertiary focus:border-secondary focus:outline-none"
                  aria-label={searchPlaceholder}
                />
              </div>
            ) : null}
            <div className="min-h-0 flex-1 overflow-y-auto">
              {visibleOptions.length === 0 ? (
                <p className="px-3 py-2.5 text-sm text-tertiary">No matches</p>
              ) : (
                visibleOptions.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                    className={`flex w-full items-center justify-between gap-3 border-b ${collectionBuildDividerClassName} px-3 ${
                      option.imageUrl ? "py-2" : "py-2.5"
                    } text-left text-sm leading-snug text-secondary last:border-b-0 hover:bg-lightGray focus:outline-none ${
                      option.value === value ? "bg-lightGray font-medium" : ""
                    }`}
                  >
                    <span className="min-w-0 truncate">{option.label}</span>
                    {option.imageUrl ? (
                      <img
                        src={option.imageUrl}
                        alt=""
                        className="h-9 w-9 shrink-0 rounded-md bg-[#F3F1EE] object-contain"
                      />
                    ) : null}
                  </button>
                ))
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default CollectionBuildSelect;
